import React, { useState } from 'react';
import styled from 'styled-components';
import { API_ENDPOINTS } from '../../api/config';
import ElevatedButton from '../Layout/ElevatedButton';

const Form = styled.form`
  max-width: 400px;
  margin: 2rem auto;
  padding: 2rem;
  border: 1px solid ${props => props.theme.buttonPrimary};
  border-radius: 8px;
  background: ${props => props.theme.cardBackground};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  color: ${props => props.theme.text};
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem 1.25rem;
  margin: 0.5rem 0;
  border: 2px solid ${props => props.theme.buttonPrimary};
  border-radius: 4px;
  font-size: 1rem;
  background: ${props => props.theme.inputBackground};
  color: ${props => props.theme.text};
  transition: all 0.3s ease;
  box-sizing: border-box;

  &::placeholder {
    color: ${props => props.theme.textMuted};
  }

  &:focus {
    outline: none;
    border-color: ${props => props.theme.buttonPrimaryHover};
    background: ${props => props.theme.inputBackgroundFocus};
    box-shadow: 0 0 8px rgba(88, 101, 242, 0.3);
  }
`;

const TokenInput = styled(Input)`
  text-align: center;
  font-size: 1.5rem;
  letter-spacing: 0.5rem;

  &::placeholder {
    letter-spacing: normal;
  }
`;

const TwoFactorPasswordReset = ({ onSuccess, onCancel }) => {
  const [step, setStep] = useState('email'); // 'email', 'token'
  const [email, setEmail] = useState('');
  const [token, setToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // For now, just proceed to token step
    // In a real implementation, you might want to send a notification
    setStep('token');
    setLoading(false);
  };

  const handleTokenSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Validate token format
    if (token.length !== 6 || !/^\d{6}$/.test(token)) {
      setError('Please enter a valid 6-digit token');
      setLoading(false);
      return;
    }

    // Validate passwords
    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters long');
      setLoading(false);
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(newPassword)) {
      setError('New password must contain uppercase, lowercase, and number');
      setLoading(false);
      return;
    }

    try {
      // Reset password with 2FA verification in one call
      const response = await fetch(API_ENDPOINTS.RESET_PASSWORD_2FA, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email,
          token,
          newPassword
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        onSuccess('Password reset successfully! You can now log in with your new password.');
      } else {
        setError(data.error || 'Password reset failed');
      }
    } catch (error) {
      console.error('Password reset error:', error);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleTokenChange = (e) => {
    const value = e.target.value.replace(/\D/g, ''); // Only allow digits
    if (value.length <= 6) {
      setToken(value);
    }
  };

  if (step === 'email') {
    return (
      <Form onSubmit={handleEmailSubmit}>
        <h2 style={{ color: '#ffffff', textAlign: 'center', textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)' }}>
          Reset Password
        </h2>

        <p style={{ textAlign: 'center', color: '#b9bbbe', marginBottom: '1rem' }}>
          Enter your email address. You'll need to provide a 2FA code to reset your password.
        </p>

        {error && (
          <div style={{
            padding: '0.75rem',
            marginBottom: '1rem',
            background: '#dc3545',
            color: 'white',
            borderRadius: '4px',
            textAlign: 'center'
          }}>
            {error}
          </div>
        )}

        <Input
          type="email"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          disabled={loading}
        />

        <ElevatedButton type="submit" style={{ width: '100%', marginTop: '1rem' }} disabled={loading}>
          {loading ? 'Sending...' : 'Continue'}
        </ElevatedButton>

        <ElevatedButton
          type="button"
          onClick={onCancel}
          style={{
            width: '100%',
            marginTop: '0.5rem',
            background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%)',
            border: '2px solid #ff4757'
          }}
        >
          Cancel
        </ElevatedButton>
      </Form>
    );
  }

  if (step === 'token') {
    return (
      <Form onSubmit={handleTokenSubmit}>
        <h2 style={{ color: '#ffffff', textAlign: 'center', textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)' }}>
          Reset Password
        </h2>

        <p style={{ textAlign: 'center', color: '#b9bbbe', marginBottom: '1rem' }}>
          Enter the 6-digit code from your authenticator app for {email} and your new password
        </p>

        {error && (
          <div style={{
            padding: '0.75rem',
            marginBottom: '1rem',
            background: '#dc3545',
            color: 'white',
            borderRadius: '4px',
            textAlign: 'center'
          }}>
            {error}
          </div>
        )}

        <TokenInput
          type="text"
          placeholder="000000"
          value={token}
          onChange={handleTokenChange}
          required
          disabled={loading}
          autoFocus
        />

        <Input
          type="password"
          placeholder="New Password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          required
          disabled={loading}
        />

        <Input
          type="password"
          placeholder="Confirm New Password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          required
          disabled={loading}
        />

        <ElevatedButton type="submit" style={{ width: '100%', marginTop: '1rem' }} disabled={loading || token.length !== 6}>
          {loading ? 'Resetting...' : 'Reset Password'}
        </ElevatedButton>

        <ElevatedButton
          type="button"
          onClick={() => setStep('email')}
          style={{
            width: '100%',
            marginTop: '0.5rem',
            background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%)',
            border: '2px solid #ff4757'
          }}
        >
          Back
        </ElevatedButton>
      </Form>
    );
  }

  return null;
};

export default TwoFactorPasswordReset;